import { Platform, Prisma, TriggerType } from "@prisma/client";
import { AllUnionMemberKeys, HttpMethod } from "./types";
import { ThreadAutoArchiveDuration } from "discord.js";

/**
 * Types for editing trigger configs
 */

export type HasField = "first-name" | "email";

type PredicateKeys =
  | "eq"
  | "neq"
  | "regex"
  | "has"
  | "hasnot"
  | "has_keywords"
  | "array_contains"
  | "contains"
  | "intersects"
  | "in"
  | "not_in"
  | "path"
  | "content";

type BanKeysExcept<K> = {
  [k in Exclude<PredicateKeys, K>]?: never;
};

export type ComplexWhenPredicate =
  | ({ eq: string | number } & BanKeysExcept<"eq">)
  | ({ neq: string | number } & BanKeysExcept<"neq">)
  | ({ regex: string } & BanKeysExcept<"regex">)
  | ({ has: HasField } & BanKeysExcept<"has">)
  // Deprecated. Use `not` instead
  | ({ hasnot: HasField } & BanKeysExcept<"hasnot">)
  | ({ has_keywords: string[] } & BanKeysExcept<"has_keywords">)
  // Deprecated. Use `contains` instead
  | ({ array_contains: string | number } & BanKeysExcept<"array_contains">)
  | ({ contains: string | number } & BanKeysExcept<"contains">)
  | ({ intersects: (string | number)[] } & BanKeysExcept<"intersects">)
  | ({ in: string[] } & BanKeysExcept<"in">)
  // Deprecated. Use `not` instead
  | ({ not_in: string[] } & BanKeysExcept<"not_in">)
  | ({ gt: number } & BanKeysExcept<"gt">)
  | ({ lt: number } & BanKeysExcept<"lt">)
  | ({ gte: number } & BanKeysExcept<"gte">)
  | ({ lte: number } & BanKeysExcept<"lte">)
  | JsonPathPredicate;

export type WhenPredicate = number | string | ComplexWhenPredicate;

export type MatchableExpr =
  | WhenUserResponseExpr
  // | WhenHttpResponseExpr
  | ReceiveWebhookConfigsExpr
  | TriggerStepIfExpr;

/**
 * Previously we have some pretty good field filtering (is this even the correct term?) by the `type` field. E.g. when `type` is "message", only `content` is valid.
 *
 * Now, in order to support recursion, we have to relax these rules by essentially allow all fields by default in any Expr. Only when `type` is specified, we get the expected field filtering.
 *
 * This seems to be fair compromise.
 *
 * One option I thought of was to move `type` to a `ExprRoot` and filter fields based on its value. However, this seems to be tricky to implement. Also, having two different types in the same query is actually a valid use case:
 *
 * ```
 * when: {
 *  userResponse: {
 *    or: [
 *      {type: 'message', ...},
 *      {type: 'button', ...}
 *    ]
 *  }
 * }
 * ```
 *
 */
export type WhenUserResponseExpr = {
  or?: WhenUserResponseExpr[];
  and?: WhenUserResponseExpr[];
  not?: WhenUserResponseExpr;
  userId?: WhenPredicate;
  channelId?: WhenPredicate;
  roleIds?: WhenPredicate;
  markAsExpectedInVars?: boolean; // See the comment in onUserResponse function
} & (
  | {
      type?: "modal-submit";
      modalId?: WhenPredicate;
      messageId?: WhenPredicate;
    }
  | {
      type?: "reaction";
      messageId?: WhenPredicate;
      content?: WhenPredicate;
    }
  | {
      type?: "reaction-remove";
      messageId?: WhenPredicate;
      content?: WhenPredicate;
    }
  | {
      type?: "message";
      content?: WhenPredicate;
    }
  | WhenButtonUserResponse
  | {
      type?: "multiple-select";
      messageId?: WhenPredicate;
      optionIds?: WhenPredicate;
    }
  | {
      type?: "captcha-result";
      userId?: WhenPredicate;
      succeeded?: boolean;
      modalId?: string;
    }
);

export type WhenButtonUserResponse = {
  type?: "button";
  buttonId?: WhenPredicate;
  optionIds?: WhenPredicate;
  messageId?: WhenPredicate;
  label?: WhenPredicate;
  emoji?: WhenPredicate;
};

export type WhenUserResponseKey = AllUnionMemberKeys<WhenUserResponseExpr>;

type WhenHttpResponseExpr = {
  // or?: WhenHttpResponseExpr[];
  // and?: WhenHttpResponseExpr[];
  // not?: WhenHttpResponseExpr;
} & (
  | {
      type?: "json";
      status?: number;
      path?: string;
      content?: WhenPredicate;
    }
  | {
      type: "text";
      status?: number;
      content?: WhenPredicate;
    }
  | {
      type: "error";
      status?: number;
      content?: WhenPredicate;
    }
);

export type WhenHttpResponse = {
  httpResponse: WhenHttpResponseExpr;
};

export type WhenClientResponse = {
  clientResponse:
    | {
        type: "success";
      }
    | {
        type: "fail";
      };
};

export type WhenHttpResponseKey = Exclude<
  AllUnionMemberKeys<WhenHttpResponse["httpResponse"]>,
  "path"
>;

export type WhenUserResponse = {
  userResponse: WhenUserResponseExpr;
};

export type When =
  | WhenUserResponse
  | WhenHttpResponse
  | WhenClientResponse
  | "email-verified";

export function whenEqual(w1: When | undefined, w2: When | undefined) {
  // TODO are there other ways?
  return w1 === w2 || JSON.stringify(w1) === JSON.stringify(w2);
}

export function when2String(when: When | undefined) {
  return when ? JSON.stringify(when) : undefined;
}

export function isWhenHttpResponse(data: any): data is WhenHttpResponse {
  return (
    typeof data === "object" &&
    data &&
    "httpResponse" in data &&
    typeof data.httpResponse === "object"
  );
}

export function isWhenUserResponse(data: any): data is WhenUserResponse {
  return (
    typeof data === "object" &&
    data &&
    "userResponse" in data &&
    typeof data.userResponse === "object"
  );
}

export function isComplexWhenPredicate(
  data: any
): data is ComplexWhenPredicate {
  return (
    typeof data === "object" &&
    data &&
    ("has" in data ||
      "eq" in data ||
      "neq" in data ||
      "regex" in data ||
      "hasnot" in data ||
      "has_keywords" in data ||
      "array_contains" in data ||
      "contains" in data ||
      "intersects" in data ||
      "in" in data ||
      "not_in" in data ||
      "path" in data)
  );
}

export function isWhenClientResponse(data: any): data is WhenClientResponse {
  return (
    typeof data === "object" &&
    data &&
    "clientResponse" in data &&
    typeof data.clientResponse === "object"
  );
}

export function isWhen(data: any): data is When {
  return (
    isWhenHttpResponse(data) ||
    isWhenUserResponse(data) ||
    data === "email-verified"
  );
}

export type TriggerTransitionOmit = Omit<
  Prisma.TriggerTransitionCreateInput,
  "step" | "workspace" | "when"
>;

export type TriggerTransitionCreate = TriggerTransitionOmit & {
  when?: WhenUserResponse;
};

export type TriggerTransitionCreateVerifyEmail = TriggerTransitionOmit & {
  when?: "email-verified";
};

export type TriggerTransitionSendMessage = TriggerTransitionOmit & {
  when?: WhenClientResponse;
};

export type TriggerTransitionCreateHttpResponse = TriggerTransitionOmit & {
  when?: WhenHttpResponse;
};

export type AnyTriggerTransition =
  | TriggerTransitionCreate
  | TriggerTransitionCreateHttpResponse
  | TriggerTransitionCreateVerifyEmail
  | TriggerTransitionSendMessage
  | TriggerTransitionOmit;

export type Position = { x: number; y: number; overlapped?: boolean };

export type StepConfigCommon = {
  delaySeconds?: number; // - Delay in seconds before executing the;
  $$editorPosition?: Position;
  $$editorPositionWhenNode?: Position;
  $$metadata?: {
    copied?: boolean;
    groupType?: "conditional_steps" | "question" | "modal"; // - It means that this step is a part of a group of steps that are related. For example ask for email has 2 send_message steps and N transitions.
  };
  sendBrandingIfNotPremium?: boolean;
};

export type SendStepsCommon = {
  channelId: string;
  ephemeral?: boolean;
  removeUrlEmbeds?: boolean;
  message: string;
  // html used for show style messages in the frontend
  $$editorHtmlMessage?: string;
};

export type ButtonOption = {
  emoji?: string;
  style?: "PRIMARY" | "SECONDARY" | "SUCCESS" | "DANGER" | "LINK"; // default to SECONDARY
  id: string;
  label: string;
  skipDefer?: boolean;
};

export type StepConfigCreateChannel = StepConfigCommon & {
  channelName: string;
  userIds: string[];
  roleIds?: string[];
  isPrivate?: boolean;
  deleteAfterMinutes?: number | null; //This is only an information flag because create_channel will not delete the channel, it will be deleted by the delete_channel step
  channelType: "onboarding" | "help"; // TODO should use CategoryType but since that enum isn't used in the schema, corresponding type isn't generated by Prisma
};

export type StepConfigCreateThread = StepConfigCommon & {
  channelId: string;
  type: "public" | "private";
  archiveDurationMinutes: ThreadAutoArchiveDuration;
  title: string;
  threadOffMessage?: string;
  reason?: string;
  metadata?: Record<string, any>;
};

export type StepConfigCreateThreadOffMessage = StepConfigCommon &
  StepConfigCreateThread & {
    messageId: string;
  };

export type StepConfigSendMessage = StepConfigCommon & SendStepsCommon;

export type StepConfigSendSurvey = StepConfigCommon &
  SendStepsCommon &
  FeedBackInteractionConfig &
  (
    | {
        type?: "emojis";
        message: string;
        // html used for show style messages in the frontend
        $$editorHtmlMessage?: string;
        options: string[];
      }
    | {
        type: "buttons";
        message: string;
        // html used for show style messages in the frontend
        $$editorHtmlMessage?: string;
        options: ButtonOption[];
      }
  );

export type StepConfigDeleteMessage = StepConfigCommon & {
  channelId: string;
  messageId: string;
};

export type StepConfigEditRoles = StepConfigCommon & {
  userIds: string[];
  addRoles?: string[]; //We should use addRoleIds, we keep this property for backward compatibility
  removeRoles?: string[]; //We should use removeRoleIds, we keep this property for backward compatibility
  addRoleIds?: string[];
  removeRoleIds?: string[];
  ephemeral?: boolean;
  acknowledge?: boolean;
  followUpMessage?: string;
  // html used for show style messages in the frontend
  $$editorHtmlFollowUpMessage?: string;
  followUpChannelId?: string;
};

export type StepConfigEditDotsTags = StepConfigCommon & {
  userIds: string[];
  addTags?: (string | number)[];
  removeTags?: (string | number)[];
};

export type StepConfigSendHttpRequest = {
  url: string;
  method: HttpMethod;
  headers?: { [key: string]: string };
  body?: string;
  feedbackChannelId?: string;
};

type FeedBackInteractionConfig = {
  feedback?: {
    disable?: boolean;
  };
};

type TriggerStepCreateChannel = {
  type: "create_channel";
  configs: StepConfigCreateChannel;
};

type TriggerStepEditRoles = {
  type: "edit_roles";
  configs: StepConfigCommon & StepConfigEditRoles;
};

type TriggerStepEditDotsTags = {
  type: "edit_dots_tags";
  configs: StepConfigCommon & StepConfigEditDotsTags;
};

export type TriggerStepSendMessage = {
  type: "send_message";
  configs: StepConfigSendMessage;
};

type TriggerStepDeleteMessage = {
  type: "delete_message";
  configs: StepConfigDeleteMessage;
};

export type TriggerStepIdle = {
  type: "idle";
  configs?: {
    reference?: string;
  } & StepConfigCommon;
};

type TriggerStepCreateThread = {
  type: "create_thread";
  configs: StepConfigCreateThread;
};

export type TriggerStepSendSurvey = {
  type: "send_survey";
  configs: StepConfigSendSurvey;
};

type TriggerStepDeleteChannel = {
  type: "delete_channel";
  configs: StepConfigCommon & {
    channelId: string;
  };
};

type TriggerStepCancelTasks = {
  type: "cancel_tasks";
  configs: StepConfigCommon & { invocationIds: string[] };
};

type TriggerStepSendWebhook = {
  type: "send_webhook";
  configs: StepConfigCommon & {
    appId: string;
    eventType: string;
    payload: object;
  };
};

type TriggerStepKickMember = {
  type: "kick_member";
  configs: StepConfigCommon & {
    reason: string;
    userId: string;
    testMode?: boolean;
  };
};

type TriggerStepChangeNickname = {
  type: "change_nickname";
  configs: StepConfigCommon & StepConfigChangeNickname;
};

export type TriggerStepTestCaptcha = {
  type: "test_captcha";
  configs: StepConfigCommon & StepConfigTestCaptcha;
};

type TriggerStepSendHttpRequest = {
  type: "send_http_request";
  configs: StepConfigCommon & StepConfigSendHttpRequest;
};

export type TriggerStepVerifyEmail = {
  type: "verify_email";
  configs: {
    email: string;
    feedbackChannelId: string;
    message: string;
  } & StepConfigCommon;
};

type TriggerStepEditChannelCategory = {
  type: "edit_channel_category";
  configs: StepConfigCommon & {
    keepChannelPermissions?: boolean;
    channelId: string;
    categoryId: string;
  };
};

export type TriggerStepSendDirectMessage = {
  type: "send_direct_message";
  configs: {
    userId: string;
    message: string;
  } & StepConfigCommon;
};
export type StepConfigMultipleSelect = StepConfigCommon &
  SendStepsCommon & {
    message: string;
  } & (
    | {
        type: "buttons";
        doneButton?: { emoji?: string; label: string };
        feedback?: {
          optionSelected: string;
          done: string;
        };
        options: {
          id: string;
          label: string;
          emoji?: string;
          skipDefer?: boolean;
        }[];
      }
    | { type: "menu"; options: { id: string; label: string }[] }
  );
export type StepConfigTestCaptcha = StepConfigCommon & {
  channelId: string;
  userId: string;
  guildId?: string;
  // html used for show style messages in the frontend
  $$succeedEditorHtmlMessage?: string;
  message: string;
  // html used for show style messages in the frontend
  $$failEditorHtmlMessage?: string;
  failMessage?: string;
  ephemeral?: boolean;
};
export type StepConfigChangeNickname = {
  userId: string;
  nickname: string;
};

export type StepConfigFindUser = {
  field: string;
  value: string;
};

export type StepConfigUserField = StepConfigFindUser & {
  userId: string;
};

type TriggerStepSendMultipleSelect = {
  type: "send_multiple_select";
  configs: StepConfigMultipleSelect;
};

export type TriggerStepOpenModal = {
  type: "open_modal";
  configs: TriggerStepOpenModalConfig;
};

export type ModalField = {
  id: string;
  type: string;
  label: string;
  style: "short" | "paragraph";
};

export type TriggerStepOpenModalConfig = {
  title: string;
  fields: ModalField[];
} & StepConfigCommon;

export type TriggerStepGetGif = {
  type: "get_gif";
  configs: TriggerStepSendGifConfig;
};

export type TriggerStepSendGifConfig = {
  //We can add other providers in the future
  provider: "giphy";
  apiKey: string;
  searchTerm: string;
  accountName?: string;
} & StepConfigCommon;

export type TriggerStepFindUser = {
  type: "find_user";
  configs: StepConfigFindUser & StepConfigCommon;
};

export type TriggerStepSaveUserField = {
  type: "save_user_field";
  configs: StepConfigUserField & StepConfigCommon;
};

type TriggerStepTypeConfigTransitions =
  | ((
      | TriggerStepCreateChannel
      | TriggerStepDeleteChannel
      | TriggerStepEditRoles
      | TriggerStepEditDotsTags
      | TriggerStepSendMessage
      | TriggerStepDeleteMessage
      | TriggerStepIdle
      | TriggerStepSendSurvey
      | TriggerStepCancelTasks
      | TriggerStepSendWebhook
      | TriggerStepKickMember
      | TriggerStepOpenModal
      | TriggerStepEditChannelCategory
      | TriggerStepSendMultipleSelect
      | TriggerStepChangeNickname
      | TriggerStepTestCaptcha
      | TriggerStepGetGif
      | TriggerStepCreateThread
      | TriggerStepFindUser
      | TriggerStepSaveUserField
    ) & { transitions: TriggerTransitionCreate[] })
  | (TriggerStepSendHttpRequest & {
      transitions: TriggerTransitionCreateHttpResponse[];
    })
  | (TriggerStepVerifyEmail & {
      transitions: TriggerTransitionCreateVerifyEmail[];
    })
  | (TriggerStepSendDirectMessage & {
      transitions: TriggerTransitionSendMessage[];
    });

export type TriggerStepIfExpr = {
  or?: TriggerStepIfExpr[];
  and?: TriggerStepIfExpr[];
  not?: TriggerStepIfExpr;
  // Ideally we'd want to specify only `WhenPredicate` here. However, the key index signature in TS requires us to provide a type that all member types can be assigned to. And also Exclude<string, 'or' | 'and' | 'not'> doesn't work!
  [ref: string]:
    | WhenPredicate
    | TriggerStepIfExpr[]
    | TriggerStepIfExpr
    | undefined;
};

export type TriggerStepIf = {
  condition: TriggerStepIfExpr;
  elseStepId: string;
};

// This array contains the steps we currently support in the trigger editor. It's important to update this array every time we implement a new step!
const SupportedSteps = [
  "send_message",
  "create_channel",
  "send_survey",
  "idle",
  "test_captcha",
  "delete_channel",
  "edit_roles",
  "create_thread",
  "send_multiple_select",
  "edit_dots_tags",
  "open_modal",
  "send_webhook",
  "kick_member",
  "change_nickname",
  "find_user",
  "save_user_field",
] as const;

// get the narrow literal type directly from an array
export type SupportedSteps = typeof SupportedSteps[number];

export function isOfTypeSupportedStep(type: any): type is SupportedSteps {
  return SupportedSteps.includes(type);
}

export type TriggerStepCreate = Omit<
  Prisma.TriggerStepCreateInput,
  | "transitions"
  | "invocations"
  | "trigger_responses"
  | "trigger"
  | "type"
  | "configs"
  | "platform"
  | "communityId"
  | "workspace"
  | "if"
> & {
  platform?: Platform;
  communityId?: string;
  name?: string;
  if?: TriggerStepIf;
} & TriggerStepTypeConfigTransitions;

export type TriggerCreate = Omit<
  Prisma.TriggerCreateInput,
  "steps" | "configs" | "parentId" | "draftId"
> & {
  steps: TriggerStepCreate[];
  liveId?: string;
} & TriggerConfigs;

export type TriggerCreateWithRootId = TriggerCreate & {
  rootId: string;
};

type TriggerConfigsCommon = {
  $$editorPosition?: Position;
};

export type RoleChangeTriggerConfigs = {
  rolesAdded?: string[];
  rolesRemoved?: string[];
} & TriggerConfigsCommon;

type MessageCreateTriggerConfigs = {
  // channelIdsWhitelist?: string[]; // Let's only have a blacklist for now since Prisma's jsonp support is a little weird.
  channelIdsBlacklist?: string[];
} & TriggerConfigsCommon;

export type ButtonClickTriggerConfigs = {
  messages: { id: string; buttonIds: string[] }[];
  // information about the button that will be sent
  // check https://app.shortcut.com/sedimentiq/story/3161/trigger-test-button-click
  $$editorButtonClickContent?: {
    htmlMessage: string;
    message: string;
    buttonText: string;
    buttonEmoji: string;
    channelId: string;
  };
} & InteractionConfigs &
  TriggerConfigsCommon;

type ReceiveWebhookConfigs = ReceiveWebhookConfigsExpr & TriggerConfigsCommon;

type JsonPathPredicate = {
  path: string;
  content: WhenPredicate;
} & BanKeysExcept<"path" | "content">;

export function isJsonPredicate(p: any): p is JsonPathPredicate {
  return typeof p?.path === "string" && "content" in p;
}

export type ReceiveWebhookConfigsExpr = {
  or?: ReceiveWebhookConfigsExpr[];
  and?: ReceiveWebhookConfigsExpr[];
  not?: ReceiveWebhookConfigsExpr;

  method?: HttpMethod;
  contentType?: "json" | "application/json"; //only support json for now
  authorization?: string;
  headers?: JsonPathPredicate;
  body?: JsonPathPredicate;
};

export type TimeTriggerConfigs = {
  schedule: string;
  timezone: string;
} & TriggerConfigsCommon;

export type TriggerConfigs =
  | {
      type: "command";
      configs?: CommandTriggerConfigs;
    }
  | {
      type: "role_change";
      configs?: RoleChangeTriggerConfigs;
    }
  | {
      type: "message_create";
      configs?: MessageCreateTriggerConfigs;
    }
  | {
      type: "button_click";
      configs?: ButtonClickTriggerConfigs;
    }
  | { type: "receive_webhook"; configs?: ReceiveWebhookConfigs }
  | { type: "time"; configs?: TimeTriggerConfigs }
  | {
      type: Exclude<
        TriggerType,
        | "command"
        | "role_change"
        | "message_create"
        | "button_click"
        | "receive_webhook"
      >;
      configs?: { [key: string]: any } & TriggerConfigsCommon;
    };

export type InteractionTriggerResponseConfig = string;

export type InteractionConfigs = {
  ephemeral?: boolean;
  skipDefer?: boolean;
  responseOnStart?: InteractionTriggerResponseConfig;
  responseOnError?: InteractionTriggerResponseConfig;
  responseOnComplete?: InteractionTriggerResponseConfig;
};

export type CommandTriggerConfigs = {
  commandName: string;
  description: string;
  roleId?: string;
  permissions?: string[];
  options?: CommandOption[];
  discordPersisted?: boolean;
  internalName?: string;
} & InteractionConfigs &
  TriggerConfigsCommon;

export type CommandOption = {
  id?: string;
  name: string;
  description: string;
  optional?: boolean;
  //We only support string for now, eventually we'll support other types
  type: "string";
};

export type DiscordCategoryCreate = Prisma.DiscordCategoryCreateInput;

export type TriggerForEditor = Omit<TriggerCreate, "isDraft"> & {
  id: string;
  updatedAt: Date;
} & (
    | { isDraft: false }
    | {
        isDraft: true;
        liveTriggerConfigs: Omit<
          TriggerCreate,
          | "steps"
          | "isDraft"
          | "platform"
          | "communityId"
          | "authorUserId"
          | "name"
          | "workspace"
        >;
      }
  );

export type UseTriggerRes = {
  trigger: TriggerForEditor;
  validationErrors: string[];
};

export type TriggerStepType = TriggerStepCreate["type"];

export const INTERNAL_STEP_PREFIX = "$$step-";
export const PLACEHOLDER_STEP_PREFIX = `${INTERNAL_STEP_PREFIX}placeholder-`;
export const EMBED_MESSAGE_STEP_PREFIX = `embed-msg-`;
export const QUESTION_MESSAGE_STEP_PREFIX = "question-msg-";
export const IF_CONDITION_STEP_PREFIX = `${INTERNAL_STEP_PREFIX}if-condition-`;
export const IF_PLACEHOLDER_STEP_PREFIX_FINAL = `if-final-${PLACEHOLDER_STEP_PREFIX}`;

export function isIfConditionStep(step: TriggerStepCreate) {
  return step.type === "idle" && isIfConditionStepId(step.id);
}
export function isPlaceholderStep(step: TriggerStepCreate) {
  return step.type === "idle" && isPlaceHolderStepId(step.id);
}
export function isPlaceHolderStepId(stepId: string) {
  return stepId.startsWith(PLACEHOLDER_STEP_PREFIX);
}
export function createPlaceholderStepId(suffix: string) {
  return `${PLACEHOLDER_STEP_PREFIX}${suffix}`;
}

export function createPlaceholderStep(suffix: string): TriggerStepCreate {
  return {
    type: "idle",
    id: createPlaceholderStepId(suffix),
    transitions: [],
  };
}

export function createEmbedMessageStepId(suffix: string) {
  return `${EMBED_MESSAGE_STEP_PREFIX}${suffix}`;
}
export function isEmbedMsgStepId(stepId: string) {
  return stepId.startsWith(EMBED_MESSAGE_STEP_PREFIX);
}

export function createQuestionMessageStepId(suffix: string) {
  return `${QUESTION_MESSAGE_STEP_PREFIX}${suffix}`;
}
export function isQuestionMsgStepId(stepId: string) {
  return stepId.startsWith(QUESTION_MESSAGE_STEP_PREFIX);
}

export function isInternalStepId(stepId: string) {
  return stepId.startsWith(INTERNAL_STEP_PREFIX);
}
type embedMessage = {
  embeds: any;
  content?: string;
};

export function isEmbedMessage(embed: any): embed is embedMessage {
  let isEmbed = false;
  const possibleKeys = [
    "title",
    "description",
    "color",
    "timestamp",
    "url",
    "author",
    "thumbnail",
    "image",
    "footer",
    "fields",
  ];

  // check if embed object has at least one required key
  if ("embeds" in embed && embed.embeds.length > 0) {
    const embedObj = embed.embeds[0];
    const keys = Object.keys(embedObj);
    keys.forEach((k) => {
      if (possibleKeys.includes(k)) {
        isEmbed = true;
      }
    });
  }

  //if it doesn't have at least one key, check that content exists to at least send a normal message
  if (!isEmbed) {
    if (
      "content" in embed &&
      typeof embed.content === "string" &&
      embed.content !== ""
    ) {
      isEmbed = true;
    }
  }
  return isEmbed;
}
// this function checks if a message is an embed json string or just a simple string
export function isJsonEmbedMessage(message: string) {
  try {
    const resJson = JSON.parse(message);
    if (isEmbedMessage(resJson)) {
      return true;
    }
  } catch (e) {
    return false;
  }

  return false;
}

export function createIfConditionStepId(suffix: string) {
  return `${IF_CONDITION_STEP_PREFIX}${suffix}`;
}

export function isIfConditionStepId(stepId: string) {
  return stepId.startsWith(IF_CONDITION_STEP_PREFIX);
}

/**
 * This function returns a primitive value from a transition
 * if the path given found a primitive value in the case it finds a
 * an object or array it will return undefined
 * Check TriggerCreate.spec.ts for examples
 * @param transition
 * @param path
 */
export function getValueByTransitionPath(
  transition: AnyTriggerTransition,
  path: string
) {
  const pathParts = path.split(".");

  let current: AnyTriggerTransition | any = transition;
  for (const pathPartsKey of pathParts) {
    const value = current[pathPartsKey];
    if (value === undefined) {
      return undefined;
    }
    current = value;
  }
  return typeof current === "object" ? undefined : current;
}
